import { AgentDto, HandoffRuleRequestDto, ToolDto } from '@shared/services/apiService/apiService';
import {
  HandoffRuleEditorValue,
  MessageGroupValue,
  VariableExtractionFieldType,
  VariableExtractionRootSchema,
  VariableExtractionSchemaNode,
  VariableExtractionPlanFormValue,
} from '@features/Admin/Agent/handoffRules/domain/contracts';
import {
  asRecord,
  createEmptyHandoffRule,
  normalizeGroupedMessages,
  resolveRuleAdapter,
  serializeGroupedMessages,
} from '@features/Admin/Agent/handoffRules/domain/ruleRegistry';

export type HandoffRulesParseResult = {
  rules: HandoffRuleEditorValue[];
  canvasAgentIds: string[];
  nodePositions: Record<string, { x: number; y: number }>;
  hasError: boolean;
};

export type HandoffRulesDocument = {
  rules: HandoffRuleRequestDto[];
  canvasAgentIds: string[];
  nodePositions: Record<string, { x: number; y: number }>;
};

export type {
  HandoffRuleEditorValue,
  MessageGroupValue,
  VariableExtractionFieldType,
  VariableExtractionRootSchema,
  VariableExtractionSchemaNode,
  VariableExtractionPlanFormValue,
};

export type HandoffCanvasNode = {
  id: string;
  position: { x: number; y: number };
  data: {
    label: string;
    agentId: string;
    isDefault?: boolean;
    isEndCallEnabled?: boolean;
  };
};

export type HandoffCanvasEdge = {
  id: string;
  source: string;
  target: string;
  data: {
    ruleId: string;
    label: string;
  };
};

export const normalizeHandoffRules = (input: unknown): HandoffRuleEditorValue[] => {
  const rules = Array.isArray(input) ? input : [];
  return rules.map((rule) => {
    const raw = asRecord(rule);
    return resolveRuleAdapter(raw).normalize(raw);
  });
};

export const parseHandoffRulesValue = (value: string | unknown): HandoffRulesParseResult => {
  if (value === '' || value === null || value === undefined) {
    return { rules: [], canvasAgentIds: [], nodePositions: {}, hasError: false };
  }

  const parseNodePositions = (input: unknown): Record<string, { x: number; y: number }> => {
    const source = asRecord(input);
    return Object.entries(source).reduce<Record<string, { x: number; y: number }>>(
      (acc, [agentId, position]) => {
        if (!agentId) return acc;
        const parsedPosition = asRecord(position);
        const x = parsedPosition.x;
        const y = parsedPosition.y;
        if (typeof x !== 'number' || Number.isNaN(x)) return acc;
        if (typeof y !== 'number' || Number.isNaN(y)) return acc;
        acc[agentId] = { x, y };
        return acc;
      },
      {},
    );
  };

  const parseDocumentShape = (input: unknown): HandoffRulesParseResult => {
    if (Array.isArray(input)) {
      const rules = normalizeHandoffRules(input);
      const canvasAgentIds = Array.from(
        new Set(rules.flatMap((rule) => [rule.fromAgentId, rule.toAgentId]).filter(Boolean)),
      ) as string[];
      return { rules, canvasAgentIds, nodePositions: {}, hasError: false };
    }

    const payload = asRecord(input);
    const rawRules = Array.isArray(payload.rules) ? payload.rules : [];
    const rules = normalizeHandoffRules(rawRules);
    const referencedAgentIds = rules.flatMap((rule) => [rule.fromAgentId, rule.toAgentId]);
    const storedCanvasAgentIds = Array.isArray(payload.canvasAgentIds)
      ? payload.canvasAgentIds.filter((agentId): agentId is string => typeof agentId === 'string')
      : [];
    const canvasAgentIds = Array.from(
      new Set([...storedCanvasAgentIds, ...referencedAgentIds].filter(Boolean)),
    ) as string[];
    return {
      rules,
      canvasAgentIds,
      nodePositions: parseNodePositions(payload.nodePositions),
      hasError: false,
    };
  };

  if (typeof value === 'string') {
    try {
      return parseDocumentShape(JSON.parse(value));
    } catch {
      return { rules: [], canvasAgentIds: [], nodePositions: {}, hasError: true };
    }
  }

  return parseDocumentShape(value);
};

export const serializeHandoffRules = (rules: HandoffRuleEditorValue[]): HandoffRuleRequestDto[] => {
  return rules.map((rule) => {
    const adapter = resolveRuleAdapter(rule.raw);
    return adapter.serialize(rule);
  });
};

export const serializeHandoffRulesDocument = (
  rules: HandoffRuleEditorValue[],
  canvasAgentIds: string[],
  nodePositions: Record<string, { x: number; y: number }>,
): HandoffRulesDocument => {
  const serializedRules = serializeHandoffRules(rules);
  const referencedAgentIds = serializedRules.flatMap((rule) => [rule.fromAgentId, rule.toAgentId]);
  return {
    rules: serializedRules,
    canvasAgentIds: Array.from(
      new Set([...canvasAgentIds, ...referencedAgentIds].filter(Boolean)),
    ) as string[],
    nodePositions,
  };
};

export { createEmptyHandoffRule, normalizeGroupedMessages, serializeGroupedMessages };

export const buildHandoffCanvasGraph = (
  rules: HandoffRuleEditorValue[],
  agents: AgentDto[],
  nodePositions?: Record<string, { x: number; y: number }>,
): { nodes: HandoffCanvasNode[]; edges: HandoffCanvasEdge[] } => {
  const agentMap = new Map(agents.map((agent) => [agent.id, agent]));
  const nodeSet = new Set<string>();

  // Always include explicitly available canvas agents, even if no rule references them yet.
  for (const agent of agents) {
    nodeSet.add(agent.id);
  }

  for (const rule of rules) {
    if (rule.fromAgentId) nodeSet.add(rule.fromAgentId);
    if (rule.toAgentId) nodeSet.add(rule.toAgentId);
  }

  const nodeIds = Array.from(nodeSet);

  const autoPositions = (() => {
    // Layered left-to-right layout based on rule direction.
    // If the graph has cycles, remaining nodes fall back to layer 0.
    const edges = rules
      .filter((rule) => Boolean(rule.fromAgentId && rule.toAgentId))
      .map((rule) => ({ from: rule.fromAgentId, to: rule.toAgentId }));

    const indegree = new Map<string, number>(nodeIds.map((id) => [id, 0]));
    const outgoing = new Map<string, string[]>(nodeIds.map((id) => [id, []]));

    for (const { from, to } of edges) {
      if (!outgoing.has(from)) outgoing.set(from, []);
      outgoing.get(from)!.push(to);
      indegree.set(to, (indegree.get(to) ?? 0) + 1);
    }

    const layer = new Map<string, number>(nodeIds.map((id) => [id, 0]));

    const queue = nodeIds
      .filter((id) => (indegree.get(id) ?? 0) === 0)
      .sort((a, b) => {
        const aName = agentMap.get(a)?.name ?? a;
        const bName = agentMap.get(b)?.name ?? b;
        return aName.localeCompare(bName);
      });

    const q: string[] = [...queue];
    while (q.length > 0) {
      const current = q.shift()!;
      const nexts = outgoing.get(current) ?? [];
      for (const next of nexts) {
        layer.set(next, Math.max(layer.get(next) ?? 0, (layer.get(current) ?? 0) + 1));
        indegree.set(next, (indegree.get(next) ?? 0) - 1);
        if ((indegree.get(next) ?? 0) === 0) {
          q.push(next);
        }
      }
    }

    // Group by layer and compute positions.
    const byLayer = new Map<number, string[]>();
    for (const id of nodeIds) {
      const l = layer.get(id) ?? 0;
      const list = byLayer.get(l) ?? [];
      list.push(id);
      byLayer.set(l, list);
    }

    for (const [l, ids] of byLayer) {
      ids.sort((a, b) => {
        const aName = agentMap.get(a)?.name ?? a;
        const bName = agentMap.get(b)?.name ?? b;
        return aName.localeCompare(bName);
      });
      byLayer.set(l, ids);
    }

    const layerKeys = Array.from(byLayer.keys()).sort((a, b) => a - b);
    const positions: Record<string, { x: number; y: number }> = {};
    const maxLayerSize = Math.max(...layerKeys.map((l) => (byLayer.get(l) ?? []).length), 1);
    const isLinearFlow = maxLayerSize === 1 && layerKeys.length === nodeIds.length;

    // For a single-path flow, add extra horizontal spacing so edge labels have room.
    const xSpacing = isLinearFlow ? 430 : 320;
    const ySpacing = isLinearFlow ? 140 : 170;
    const xStart = 80;
    const yStart = isLinearFlow ? 220 : 60;

    for (const l of layerKeys) {
      const ids = byLayer.get(l) ?? [];
      for (let i = 0; i < ids.length; i += 1) {
        const id = ids[i];
        positions[id] = {
          x: xStart + l * xSpacing,
          y: isLinearFlow ? yStart : yStart + i * ySpacing,
        };
      }
    }

    return positions;
  })();

  const nodes: HandoffCanvasNode[] = nodeIds.map((agentId, index) => {
    const agent = agentMap.get(agentId);

    return {
      id: agentId,
      position: nodePositions?.[agentId] ??
        autoPositions[agentId] ?? { x: 80 + index * 40, y: 80 + index * 30 },
      data: {
        label: agent?.name || agentId,
        agentId,
        isDefault: (agent as (AgentDto & { isDefault?: boolean }) | undefined)?.isDefault,
        isEndCallEnabled: Boolean(
          (agent as (AgentDto & { tools?: ToolDto[] }) | undefined)?.tools?.some(
            (tool) => tool.type === 'end-call',
          ),
        ),
      },
    };
  });

  const edges: HandoffCanvasEdge[] = rules
    .filter((rule) => Boolean(rule.fromAgentId && rule.toAgentId))
    .map((rule) => ({
      id: `edge-${rule.id}-${rule.fromAgentId}-${rule.toAgentId}`,
      source: rule.fromAgentId,
      target: rule.toAgentId,
      data: {
        ruleId: rule.id,
        label: rule.name || 'handoff',
      },
    }));

  return { nodes, edges };
};
